import {parseInteger} from '@benchling/js-utils';

import {MonomerFilter, MonomerFilterQualifier, Operator} from '../SearchConstants';

export interface MonomerLocalFilterState {
  filter: MonomerFilter;
  qualifierValueError: string;
  qualifierValueText: string;
}
export enum MonomerLocalFilterActionType {
  UPDATE_VALUE = 'UPDATE_VALUE',
  UPDATE_OPERATOR = 'UPDATE_OPERATOR',
  UPDATE_QUALIFIER = 'UPDATE_QUALIFIER',
  UPDATE_QUALIFIER_VALUE = 'UPDATE_QUALIFIER_VALUE',
}
interface LocalFilterOperatorAction {
  type: MonomerLocalFilterActionType.UPDATE_OPERATOR;
  operator: Operator;
}
interface LocalFilterQualifierAction {
  type: MonomerLocalFilterActionType.UPDATE_QUALIFIER;
  qualifier: MonomerFilterQualifier;
}
interface LocalFilterQualifierValueAction {
  type: MonomerLocalFilterActionType.UPDATE_QUALIFIER_VALUE;
  textValue: string;
}
interface LocalFilterValueAction {
  type: MonomerLocalFilterActionType.UPDATE_VALUE;
  value: string;
}
export type MonomerLocalFilterAction =
  | LocalFilterOperatorAction
  | LocalFilterQualifierAction
  | LocalFilterQualifierValueAction
  | LocalFilterValueAction;

const COUNT_QUALIFIERS = [MonomerFilterQualifier.MAX_COUNT, MonomerFilterQualifier.MIN_COUNT];

function parseQualifierValue(
  textValue: string,
  valIsArray: boolean
): [Array<number> | number | undefined, string] {
  const numbers = textValue.split(',').map((p) => parseInteger(p));
  if (valIsArray && numbers.every((p) => p && p > 0)) {
    // User-facing positions are 1-indexed rather than 0-indexed, so we convert here
    return [(numbers as Array<number>).map((n) => n - 1), ''];
  } else if (!valIsArray && numbers.length === 1 && numbers[0] && numbers[0] > 0) {
    return [numbers[0], ''];
  } else {
    return [undefined, valIsArray ? 'Invalid value for position(s)' : 'Invalid value for count'];
  }
}

export default function localFilterStateReducer(
  state: MonomerLocalFilterState,
  action: MonomerLocalFilterAction
): MonomerLocalFilterState {
  switch (action.type) {
    case MonomerLocalFilterActionType.UPDATE_OPERATOR: {
      const newValue = state.filter.value;
      let newQualifier = state.filter.qualifier;
      let newQualifierValue = state.filter.qualifierValue;
      if (action.operator === Operator.IS_NOT && COUNT_QUALIFIERS.includes(state.filter.qualifier)) {
        newQualifier = MonomerFilterQualifier.ANY_POS;
        newQualifierValue = undefined;
      }
      return {
        ...state,
        filter: {
          ...state.filter,
          value: newValue,
          operator: action.operator,
          qualifier: newQualifier,
          qualifierValue: newQualifierValue,
        },
      };
    }
    case MonomerLocalFilterActionType.UPDATE_QUALIFIER: {
      let newQualifierValue = state.filter.qualifierValue;
      let newQualifierValueText = state.qualifierValueText;
      if (
        !(COUNT_QUALIFIERS.includes(state.filter.qualifier) && COUNT_QUALIFIERS.includes(action.qualifier))
      ) {
        newQualifierValue = undefined;
        newQualifierValueText = '';
      }
      return {
        ...state,
        qualifierValueText: newQualifierValueText,
        filter: {...state.filter, qualifier: action.qualifier, qualifierValue: newQualifierValue},
      };
    }
    case MonomerLocalFilterActionType.UPDATE_QUALIFIER_VALUE: {
      let error = '';
      let value = state.filter.qualifierValue;
      if (action.textValue.trim().length > 0) {
        const valIsArray = state.filter.qualifier === MonomerFilterQualifier.AT_POS;
        [value, error] = parseQualifierValue(action.textValue, valIsArray);
      }
      return {
        ...state,
        filter: {...state.filter, qualifierValue: value},
        qualifierValueText: action.textValue,
        qualifierValueError: error,
      };
    }
    case MonomerLocalFilterActionType.UPDATE_VALUE:
      return {...state, filter: {...state.filter, value: action.value}};

    default:
      return state;
  }
}
